import React from "react";
import { Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList, ComposedChart } from "recharts";
import { Box, Typography } from "@mui/material";
import { useContext } from "react";
import { AuthContext } from "../Auth/AuthContext";
import { formatCurrency } from "../utils/currencyFormatter";

const TotalStats = ({ totalIncome, totalExpenses, totalInvestments, totalSavings }) => {
  const formattedIncome = parseFloat(totalIncome).toFixed(2);
  const formattedExpenses = parseFloat(totalExpenses).toFixed(2);
  const formattedInvestments = parseFloat(totalInvestments).toFixed(2);
  const formattedSavings = parseFloat(totalSavings).toFixed(2);
  const { userCurrency } = useContext(AuthContext);

  const data = [
    {
      name: `Income: ${formattedIncome}`,
      value: parseFloat(formattedIncome),
      fullName: "Income",
      color: "#4caf50"
    },
    {
      name: `Expenses: ${formattedExpenses} (${((totalExpenses / totalIncome) * 100).toFixed(2)}%)`,
      value: parseFloat(formattedExpenses),
      fullName: "Expenses",
      color: "#f44336"
    },
    {
      name: `Investments: ${formattedInvestments} (${((totalInvestments / totalIncome) * 100).toFixed(2)}%)`,
      value: parseFloat(formattedInvestments),
      fullName: "Investments",
      color: "#ff9800"
    },
    {
      name: `Savings: ${formattedSavings} (${((totalSavings / totalIncome) * 100).toFixed(2)}%)`,
      value: parseFloat(formattedSavings),
      fullName: "Savings",
      color: "#2196f3"
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const value = payload[0].value;
      
      if (data.fullName === "Income") {
        return (
          <div style={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.95)', 
            padding: '12px', 
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            fontSize: '14px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            fontWeight: '500'
          }}>
            <p style={{ margin: 0, color: data.color }}>{`${data.fullName}: ${formatCurrency(value, userCurrency)}`}</p>
          </div>
        );
      }
      
      const percentage = ((value / totalIncome) * 100).toFixed(2);
      return (
        <div style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
          padding: '12px', 
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          fontSize: '14px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontWeight: '500'
        }}>
          <p style={{ margin: 0, color: data.color }}>{`${data.fullName}: ${formatCurrency(value, userCurrency)} (${percentage}%)`}</p>
        </div>
      );
    }
    return null;
  };

  const CustomizedLabel = ({ x, y, width, height, value }) => {
    return (
      <text 
        x={x + width / 2} 
        y={y + height / 2} 
        fill="black" 
        textAnchor="middle" 
        dominantBaseline="central"
        fontSize="14"
      >
        {value}
      </text>
    );
  };

  return (
    <Box sx={{ width: "100%", textAlign: "center" }}>
      <Typography variant="h6" gutterBottom>Total Stats</Typography>
      <Box sx={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 0 }}>
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 14, fill: 'black' }}
              angle={0}
              textAnchor="middle"
              height={80}
              interval={0}
              tickMargin={7}
            />
            <YAxis tick={{ fontSize: 14, fill: 'black' }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" >
              <LabelList content={<CustomizedLabel />} />
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default TotalStats;